import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../components/api.js';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Função simplificada para verificar se o token é válido
  const isTokenValid = (token) => {
    if (!token) return false;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      // Adiciona margem de 5 minutos para renovação
      return payload.exp > (currentTime + 300);
    } catch (error) {
      console.error('Erro ao validar token:', error);
      return false;
    }
  };

  // Função para renovar o token automaticamente
  const refreshToken = async () => {
    try {
      const refresh = localStorage.getItem('refresh_token');
      if (!refresh) {
        throw new Error('Refresh token não encontrado');
      }

      const response = await fetch('' + API_BASE_URL + '/api/token/refresh/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh }),
      });

      if (!response.ok) {
        throw new Error('Falha ao renovar token');
      }

      const data = await response.json();
      localStorage.setItem('access_token', data.access);
      return data.access;
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      logout();
      return null;
    }
  };

  // Logout simplificado - só remove tokens locais
  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_roles');
    setIsAuthenticated(false);
    setUser(null);
    navigate('/login');
  };

  // Login simplificado
const login = async (username, password) => {
  try {
    const response = await fetch('' + API_BASE_URL + '/api/login/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error('Login failed. Please check your credentials.');
    }

    const data = await response.json();

    console.log('RESPOSTA DA API LOGIN:', data);
    console.log('ROLES DA API:', data.roles);

    localStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    localStorage.setItem('user_roles', JSON.stringify(data.roles || []));

    setIsAuthenticated(true);
    setUser({ username, roles: data.roles || [] });

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

  // Função para requisições autenticadas (renovação automática)
  const authenticatedFetch = async (url, options = {}) => {
    let token = localStorage.getItem('access_token');
    
    // Verifica se precisa renovar o token
    if (!isTokenValid(token)) {
      console.log('Token expirando, renovando...');
      token = await refreshToken();
      if (!token) {
        throw new Error('Não foi possível renovar o token');
      }
    }

    // Cria um objeto de headers para poder modificá-lo
    let headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };

    // Remove Content-Type se o corpo for FormData
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    } else {
      headers['Content-Type'] = 'application/json';
    }

    return fetch(url, {
      ...options,
      headers: headers,
    });
  };

  // Verificação inicial simplificada
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('access_token');
      const refresh = localStorage.getItem('refresh_token');

      if (!token || !refresh) {
        setIsLoading(false);
        return;
      }

      // Se o token ainda é válido, considera logado
      if (isTokenValid(token)) {
        setIsAuthenticated(true);
        const roles = JSON.parse(localStorage.getItem('user_roles') || '[]');
        setUser({ username: 'user', roles });
      } else {
        // Tenta renovar o token uma vez
        const newToken = await refreshToken();
        if (newToken) {
          setIsAuthenticated(true);
          const roles = JSON.parse(
            localStorage.getItem('user_roles') || '[]'
          );
          setUser({ username: 'user', roles });
        }
      }
      
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const value = {
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    authenticatedFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};